/**
 * 商务风尚模版 - 列表页
 *
 * 设计理念：商务目录式侧栏 + 行式条目列表
 * 与 sunset（顶部药丸标签栏 + 瀑布流 + 加载更多）完全不同的排版骨架：
 * - 顶部：商务面包屑 + 深色页头条
 * - 主体：左侧分类目录侧栏（深墨绿标题条 + 竖排分类）
 * - 列表：右侧行式条目（缩略图 + 标题 + 摘要 + 元信息行）
 * - 分页：数字页码（非加载更多）
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/templates/executive/components/Navbar";
import ExecutiveFooter from "@/templates/executive/components/Footer";
import {
  getSiteCategories,
  getListings,
  getNavCategories,
  type FrontendCategory,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";

const SORT_OPTIONS = [
  { key: "latest", label: "最新发布" },
  { key: "popular", label: "最多浏览" },
  { key: "alpha", label: "名称排序" },
] as const;

export default function ExecutiveCategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();

  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getSiteCategories().then(setCategories).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (slug && categories.length > 0) {
      const found = categories.find((c) => c.slug === slug || c.id === slug);
      if (found) setActiveCategoryId(found.id);
    }
  }, [slug, categories]);

  // 加载数据（切换分类/排序时重置）
  useEffect(() => {
    if (siteLoading || !siteId) return;
    setLoading(true);
    setCurrentPage(1);
    const params: Record<string, any> = { page: 1, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    getListings(params)
      .then((data) => {
        setListings(data.list || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId, siteLoading, activeCategoryId, sortBy]);

  // 页码切换
  const handlePageChange = async (page: number) => {
    if (page < 1 || page > totalPages) return;
    setLoading(true);
    setCurrentPage(page);
    const params: Record<string, any> = { page, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    try {
      const data = await getListings(params);
      setListings(data.list || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const currentCategoryName = useMemo(() => {
    if (activeCategoryId) {
      const cat = categories.find((c) => c.id === activeCategoryId);
      return cat?.name || "全部分类";
    }
    return "全部分类";
  }, [activeCategoryId, categories]);

  const sortedListings = useMemo(() => {
    const list = [...listings];
    if (sortBy === "popular") list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    if (sortBy === "alpha") list.sort((a, b) => a.title.localeCompare(b.title, "zh"));
    return list;
  }, [listings, sortBy]);

  const displayCategories = navCategories.length > 0 ? navCategories : categories;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // 页码列表
  const pageNumbers = useMemo(() => {
    const pages: number[] = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [currentPage, totalPages]);

  return (
    <div className="min-h-screen bg-[#F5F6F4]">
      <Navbar />

      {/* ======== 面包屑 ======== */}
      <div className="bg-white border-b border-[#E2E8E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 text-sm py-4">
            <Link to="/" className="text-gray-400 hover:text-[#C2A04B] transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs text-gray-300" />
            <span className="text-[#1F4E3D] font-semibold">{currentCategoryName}</span>
          </nav>
        </div>
      </div>

      {/* ======== 页头条 ======== */}
      <div className="bg-gradient-to-r from-[#1F4E3D] to-[#17382B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="w-1.5 h-10 bg-[#C2A04B] rounded-full" />
              <div>
                <h1 className="text-2xl font-black text-white">{currentCategoryName}</h1>
                <p className="text-white/60 text-sm mt-1">
                  共 <span className="text-[#DDC583] font-bold">{total}</span> 条信息
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/publish")}
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] text-sm font-bold rounded-sm hover:from-[#D8B86A] hover:to-[#B8912F] transition-all"
            >
              <i className="ri-add-line text-base" />发布信息
            </button>
          </div>
        </div>
      </div>

      {/* ======== 主体：左侧栏 + 右列表 ======== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

          {/* ── 左：分类目录侧栏 ── */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-md border border-[#E2E8E0] overflow-hidden sticky top-24">
              <div className="flex items-center gap-2 px-5 py-3.5 bg-[#1F4E3D]">
                <i className="ri-menu-fold-line text-[#DDC583]" />
                <span className="text-sm font-bold text-white">分类目录</span>
              </div>
              <nav className="py-2">
                <button
                  onClick={() => setActiveCategoryId("")}
                  className={`flex items-center gap-3 w-full px-5 py-3 text-sm font-medium transition-colors border-l-[3px] ${
                    !activeCategoryId
                      ? "border-[#C2A04B] bg-[#1F4E3D]/5 text-[#1F4E3D]"
                      : "border-transparent text-gray-600 hover:bg-[#F7F8F6] hover:text-[#1F4E3D]"
                  }`}
                >
                  <i className="ri-apps-2-line text-[#C2A04B]" />全部
                </button>
                {displayCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategoryId(cat.id === activeCategoryId ? "" : cat.id)}
                    className={`flex items-center gap-3 w-full px-5 py-3 text-sm font-medium transition-colors border-l-[3px] ${
                      activeCategoryId === cat.id
                        ? "border-[#C2A04B] bg-[#1F4E3D]/5 text-[#1F4E3D]"
                        : "border-transparent text-gray-600 hover:bg-[#F7F8F6] hover:text-[#1F4E3D]"
                    }`}
                  >
                    {cat.icon && <i className={`${cat.icon} text-[#C2A04B] text-sm`} />}
                    <span className="flex-1 text-left truncate">{cat.name}</span>
                  </button>
                ))}
              </nav>

              {/* 排序 */}
              <div className="border-t border-[#EDF0EC] px-5 py-4">
                <label className="text-xs font-semibold text-gray-500 flex items-center gap-1.5 mb-2">
                  <i className="ri-sort-desc text-[#C2A04B]" />排序方式
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2.5 bg-[#F7F8F6] rounded-sm text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#C2A04B] cursor-pointer border border-[#E2E8E0]"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </aside>

          {/* ── 右：行式条目列表 ── */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-white rounded-md border border-[#E2E8E0] p-4">
                    <div className="flex gap-4">
                      <div className="w-24 h-24 rounded-sm bg-gray-200/70 aspect-square shrink-0" />
                      <div className="flex-1 space-y-3 py-1">
                        <div className="w-3/4 h-4 bg-gray-200 rounded" />
                        <div className="w-full h-3 bg-gray-100 rounded" />
                        <div className="w-1/2 h-3 bg-gray-100 rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : sortedListings.length > 0 ? (
              <>
                <div className="space-y-4">
                  {sortedListings.map((listing) => (
                    <Link
                      key={listing.id}
                      to={`/listing/${listing.id}`}
                      className="group flex gap-4 p-4 bg-white rounded-md border border-[#E2E8E0] hover:border-[#C2A04B] hover:shadow-lg transition-all"
                    >
                      {/* 正方形缩略图 */}
                      <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-sm bg-[#EDF0EC] overflow-hidden shrink-0 relative aspect-square">
                        {listing.images?.[0] ? (
                          <img
                            src={listing.images[0]}
                            alt={listing.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                            <i className="ri-image-line text-[#C2A04B]/50 text-2xl" />
                            <span className="text-[10px] text-[#C2A04B]/60">暂无图片</span>
                          </div>
                        )}
                        {listing.is_pinned && (
                          <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-sm bg-[#C2A04B] text-[#1F4E3D] font-bold shadow-sm">
                            置顶
                          </span>
                        )}
                      </div>

                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <h3 className="text-sm sm:text-base font-bold text-gray-800 line-clamp-1 group-hover:text-[#1F4E3D] transition-colors">
                          {listing.title}
                        </h3>
                        <p className="text-xs text-gray-400 mt-2 line-clamp-2 leading-relaxed hidden sm:block">
                          {listing.content?.substring(0, 80) || "暂无描述"}
                        </p>
                        <div className="flex items-center gap-3 mt-3 flex-wrap">
                          <span className="text-[11px] px-2 py-0.5 bg-[#1F4E3D]/5 text-[#1F4E3D] rounded-sm font-medium border border-[#1F4E3D]/10">
                            {listing.category?.name || "未分类"}
                          </span>
                          <span className="text-[11px] text-gray-400 flex items-center gap-1">
                            <i className="ri-eye-line text-[#C2A04B]" />{listing.view_count || 0}
                          </span>
                          <span className="text-[11px] text-gray-400 flex items-center gap-1">
                            <i className="ri-time-line text-[#C2A04B]" />{formatDate(listing.created_at)}
                          </span>
                        </div>
                      </div>

                      <div className="hidden sm:flex items-center justify-center pl-2">
                        <i className="ri-arrow-right-s-line text-2xl text-gray-300 group-hover:text-[#C2A04B] transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>

                {/* 数字分页 */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="w-9 h-9 rounded-sm bg-white border border-[#E2E8E0] text-gray-500 hover:border-[#C2A04B] hover:text-[#C2A04B] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <i className="ri-arrow-left-s-line" />
                    </button>
                    {pageNumbers.map((p) => (
                      <button
                        key={p}
                        onClick={() => handlePageChange(p)}
                        className={`w-9 h-9 rounded-sm text-sm font-medium transition-colors ${
                          p === currentPage
                            ? "bg-[#1F4E3D] text-[#DDC583] border border-[#1F4E3D]"
                            : "bg-white border border-[#E2E8E0] text-gray-600 hover:border-[#C2A04B] hover:text-[#C2A04B]"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 rounded-sm bg-white border border-[#E2E8E0] text-gray-500 hover:border-[#C2A04B] hover:text-[#C2A04B] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <i className="ri-arrow-right-s-line" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 bg-white rounded-md border border-[#E2E8E0]">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-sm bg-[#1F4E3D]/5 border border-[#1F4E3D]/10">
                  <i className="ri-inbox-line text-2xl text-[#C2A04B]" />
                </div>
                <p className="text-gray-500 font-medium">该分类暂无信息</p>
                <p className="text-gray-400 text-sm mt-1">换个分类试试，或返回首页</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 mt-5 px-6 py-3 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] text-sm font-bold rounded-sm hover:from-[#D8B86A] hover:to-[#B8912F] transition-all shadow-md"
                >
                  <i className="ri-arrow-left-line" />返回首页
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <ExecutiveFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
